<template>
  <div>
    <bs-card class="card-info" :icon="['fal', 'calendar-alt']" :label="$t('labels.' + camelToSnake($options.name))">
      <template v-slot:header>
        <bs-button v-if="auth.can['edit_customer_schedules']" class="btn-sm btn-info" @click="create">{{ $t('labels.action_booking', { action: $t('labels.create') }) }}</bs-button>
      </template>
      <div>
        <Calendar :data-source="calData"
                  @mouse-on-day="mouseOnDay"
                  @mouse-out-day="mouseOutDay"
                  @click-day="clickDay"
                  :key="calKey"
        >
        </Calendar>
      </div>
      <bs-table v-bind="{dataLength, loading}" :cols="8">
        <template v-slot:thead>
          <tr>
            <bs-th :label="$t('labels.due_at')" :sortable="resourceState.sort" :col="'due_at'" @click="applySort"></bs-th>
            <bs-th :label="$tc('labels.field_worker', 0)" :sortable="resourceState.sort" :col="'field_worker'" @click="applySort"></bs-th>
            <bs-th :label="$tc('labels.property', 1)" :sortable="resourceState.sort"></bs-th>
            <th></th>
            <th></th>
            <th></th>
            <th></th>
            <th></th>
          </tr>
        </template>
        <tr v-for="r in resources.data" :key="r.id">
          <td>
            <span v-if="r.multi_day">
              <span>{{ r.due_at | luxon:format('MMM d') }}</span>
              <span> - </span>
              <span>{{ r.end_at | luxon:format('MMM d') }}</span>
            </span>
            <span v-else>{{ r.due_at | luxon:format('ccc MMM d, y h:mma') }}</span>
          </td>
          <td>{{ joinObjects(', ', 'name', r.users) }}</td>
          <td><span v-if="r.property">{{ r.property.label }}</span></td>
          <td v-tooltip.top="taskGroupTip(r.task_groups)" class="text-center">
            {{ $t('labels.resource_count', { resource: $tc('labels.task_group', r.task_groups.length), count: r.task_groups.length }) }}
          </td>
          <td v-tooltip.top="taskGroupTip(r.all_tasks)" class="text-center">
            {{ $t('labels.resource_count', { resource: $tc('labels.task', r.all_tasks.length), count: r.all_tasks.length }) }}
          </td>
          <td v-if="r.repeat" v-tooltip.top="$t('messages.booking_repeats', { frequency: $t('labels.' + r.repeat) })" class="text-center">
            <fa-icon :icon="['fal', 'repeat']" />
          </td>
          <td v-else></td>
          <td class="text-center">
            <bs-badge class="badge-success" v-if="r.completed">
              {{ $t('labels.completed')}}
            </bs-badge>
            <bs-badge class="badge-secondary" v-else>
              {{ $t('labels.pending')}}
            </bs-badge>
          </td>
          <td class="t3">
            <bs-action v-if="r.completed" :icon="['fas', 'eye']" :tip="$t('labels.action_resource', { action: $t('labels.view'), resource: $t('labels.report') })" class="btn-warning" @click="viewReport(r)"></bs-action>
            <bs-action v-if="auth.can['edit_customer_schedules']" :icon="['fal', 'pencil']" :tip="$t('labels.action_booking', { action: $t('labels.edit') })" class="btn-info" @click="update(r)"></bs-action>
            <bs-action v-if="auth.can['edit_customer_schedules']" :icon="['fal', 'trash']" :tip="$t('labels.action_booking', { action: $t('labels.destroy') })" class="btn-danger" @click="destroy(r)"></bs-action>
          </td>
        </tr>
      </bs-table>
    </bs-card>
    <bs-modal :name="'create-' + $options.name" :label="$t('labels.action_booking', { action: resource.id ? $t('labels.edit') : $t('labels.create') })" class="allow-overflow">
      <booking-modal :resource="resource" @submit="submit" />
    </bs-modal>
    <bs-modal :name="'report-booking'" :label="$t('labels.report')">
      <booking-report :report="activeBooking" />
    </bs-modal>
    <bs-modal :name="'destroy-' + $options.name" :label="$t('labels.action_' + camelToSnake(singular($options.name)), { action: $t('labels.destroy') })">
      <bs-form :destroy="$t('labels.destroy')" @submit="destruction">
        {{ $t('messages.delete', { resource: $t('labels.booking') }) }}
      </bs-form>
    </bs-modal>
    <portal to="app-footer">
      <pagination :data="resources" :state="resourceState" @pushPage="index"></pagination>
    </portal>
  </div>
</template>

<script>
import helpers from '../../helpers'
import { mapState, mapMutations } from 'vuex'
import Calendar from 'v-year-calendar'
import { DateTime } from 'luxon'
import tippy from 'tippy.js'

export default {
  name: 'Schedules',
  props: ['customer'],
  components: {
    Calendar
  },
  data () {
    return {
      loading: false,
      dataLoading: false,
      activeId: null,
      customBooking: false,
      activeBooking: {},
      resource: {
        customer_id: null,
        property_id: null,
        multi_day: null,
        due_at: null,
        end_at: null,
        repeat: null,
        users: [],
        tasks: [],
        task_groups: [],
        description: null,
        notify_scheduled: true,
        notify_arrived: true,
        notify_left: true
      },
      calKey: 0,
      calTooltip: null,
      calData: [],
      dateFormat: 'yyyy-MM-dd',
      dateTimeFormat: 'yyyy-MM-dd\'T\'hh:mm'
    }
  },
  mounted () {
    this.index(this.page)
    this.calDataSource()
  },
  computed: {
    ...mapState({
      auth: state => state.auth,
      resourceState: state => state.schedulesState,
      store: state => state.schedulesStore,
      resources: state => state.schedules,
      users: state => state.usersStore,
      submitting: state => state.submitting
    }),
    dataLength () {
      let length = 0
      if (this.resources.data) length = this.resources.data.length
      return Math.max(this.resourceState.tableRows - length, 0)
    },
    fieldWorkers () {
      return this._.orderBy(this.users, ['name'])
    }
  },
  watch: {
    'resource.multi_day': function (newVal) {
      if (this.resource.due_at) {
        if (newVal) {
          this.resource.due_at = DateTime.fromFormat(this.resource.due_at, this.dateTimeFormat).toFormat(this.dateFormat)
          this.resource.end_at = this.resource.end_at ?? this.resource.due_at
        } else {
          const dueAt = DateTime.fromFormat(this.resource.due_at, this.dateFormat).toFormat(this.dateTimeFormat)
          if (dueAt !== 'Invalid DateTime') {
            this.resource.due_at = dueAt
          }
        }
      }
    }
  },
  methods: {
    joinObjects: helpers.joinObjects,
    taskGroupTip (groups) {
      const gs = []
      groups.forEach((g) => {
        gs.push(g.label)
      })
      return gs.join(', ')
    },
    mouseOnDay: function (e) {
      if (e.events.length > 0) {
        let content = ''

        for (const i in e.events) {
          content += '<div class="event-tooltip-content">' +
              '<div class="event-name" style="color:' + e.events[i].color + '">' + e.events[i].name + '</div>' +
              '<div class="event-details">' + e.events[i].details + '</div>' +
              '</div>'
        }

        if (this.calTooltip != null) {
          this.calTooltip.destroy()
          this.calTooltip = null
        }

        this.calTooltip = tippy(e.element, {
          placement: 'right',
          content: content,
          allowHTML: true,
          arrow: true
        })
        this.calTooltip.show()
      }
    },
    mouseOutDay: function () {
      if (this.calTooltip !== null) {
        this.calTooltip.destroy()
        this.calTooltip = null
      }
    },
    clickDay (e) {
      if (e.events.length > 0) {
        const booking = this._.find(this.store, ['id', e.events[0].id])
        this.update(booking)
      } else {
        this.create()
        this.resource.due_at = DateTime.fromFormat(e.date.toLocaleDateString(), 'dd/MM/yyyy').set({ hour: 8 }).toFormat(this.dateTimeFormat)
        this.resource.end_at = DateTime.fromFormat(e.date.toLocaleDateString(), 'dd/MM/yyyy').set({ hour: 10 }).toFormat(this.dateTimeFormat)
      }
    },
    calDataSource () {
      this.calData = []
      this.store.forEach((r) => {
        const start = r.due_at
        let end = r.due_at
        if (r.multi_day) end = r.end_at
        this.calData.push({
          id: r.id,
          name: helpers.joinObjects(', ', 'name', r.users),
          details: this.taskGroupTip(r.tasks),
          startDate: DateTime.fromISO(start),
          endDate: DateTime.fromISO(end)
        })
      })
      this.calKey += 1
    },
    ...mapMutations([
      'pushState'
    ]),
    viewReport (r) {
      this.activeBooking = r
      this.$modal.show('report-booking')
    },
    applySort (val) {
      this.pushState({ view: this.$options.name, key: 'sort', data: val })
      this.index()
    },
    index (page, count) {
      if (page) this.pushState({ view: this.$options.name, key: 'page', data: page })
      if (count) this.pushState({ view: this.$options.name, key: 'tableRows', data: count })
      this.loading = true
      this.$store.dispatch('index', {
        collection: this.$options.name,
        path: 'customers',
        id: this.customer,
        tag: 'schedules',
        data: {
          icon: ['fal', 'calendar-alt']
        }
      }).then(() => {
        this.loading = false
      })
    },
    create () {
      this.$store.commit('axiosError', null)
      this.resource.users = []
      this.resource.tasks = []
      this.resource.task_groups = []
      this.$root.clearData(this.resource)
      this.resource.customer_id = this.customer
      this.$modal.show('create-' + this.$options.name)
    },
    destroy (o) {
      this.activeId = o.id
      this.$modal.show('destroy-' + this.$options.name)
    },
    update (o) {
      this.$store.commit('axiosError', null)
      this.resource = JSON.parse(JSON.stringify(o))
      this.$modal.show('create-' + this.$options.name)
    },
    submit () {
      this.$store.dispatch(this.resource.id ? 'update' : 'store', { collection: this.$options.name, path: 'customers', id: this.customer, tag: 'schedules', resource: this.resource }).then(() => {
        this.$modal.hide('create-' + this.$options.name)
        this.calDataSource()
      })
    },
    destruction () {
      this.$store.dispatch('destroy', { collection: this.$options.name, path: 'customers', id: this.customer, tag: 'schedules', tag_id: this.activeId }).then(() => {
        this.$modal.hide('destroy-' + this.$options.name)
      })
    }
  }
}
</script>
